import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import { PhotoIcon, EyeIcon, VideoCameraIcon, SpeakerWaveIcon, SparklesIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
import { useVideoEditor } from './context/VideoEditorContext';
import PreviewTab from './tabs/PreviewTab';
import ImagesTab from './tabs/ImagesTab';
import AudioTab from './tabs/AudioTab';
import VideoTab from './tabs/VideoTab';
import EffectsTab from './tabs/EffectsTab';
import VideoClipEditor from './VideoClipEditor';
import ImageDurationEditor from './ImageDurationEditor';
import { ClipInfiniteCanvasModal } from './ClipInfiniteCanvas';
import { IEpisode, IVideoClip } from '@packages/global';

type PreviewPopupTabId = 'preview' | 'images' | 'audio' | 'effects' | 'video';
type PreviewSideTabId = PreviewPopupTabId | 'canvas';

interface PreviewAreaWithTabsProps {
  selectedVideoClip: IVideoClip | null;
  episode: IEpisode | null;
  refreshVideoClip: (clipId: string) => Promise<void>;
  refreshEpisode?: () => Promise<void>;
  imageFrameType: 'start' | 'end' | 'storyboard';
  setImageFrameType: (type: 'start' | 'end' | 'storyboard') => void;
}

export default function PreviewAreaWithTabs({
  selectedVideoClip,
  episode,
  refreshVideoClip,
  imageFrameType,
  setImageFrameType
}: PreviewAreaWithTabsProps) {
  const { state, actions } = useVideoEditor();
  const { t } = useTranslation(['editor', 'common']);
  const [modalImageFrameType, setModalImageFrameType] = useState<'start' | 'end' | 'storyboard'>(imageFrameType);
  const [canvasOpen, setCanvasOpen] = useState(false);

  useEffect(() => {
    if (state.activeTab === 'images') {
      setModalImageFrameType(imageFrameType);
    }
  }, [state.activeTab, imageFrameType]);

  const tabs: Array<{
    id: PreviewSideTabId;
    label: string;
    icon: typeof EyeIcon;
    hasContent: boolean;
    color: string;
  }> = selectedVideoClip ? [
    {
      id: 'preview',
      label: t('previewArea.preview'),
      icon: EyeIcon,
      hasContent: true,
      color: 'text-blue-400'
    },
    {
      id: 'images',
      label: t('previewArea.images'),
      icon: PhotoIcon,
      hasContent: !!(selectedVideoClip.startImage || selectedVideoClip.endImage),
      color: 'text-green-400'
    },
    {
      id: 'audio',
      label: t('previewArea.audio'),
      icon: SpeakerWaveIcon,
      hasContent: !!selectedVideoClip.scriptAudioUrl,
      color: 'text-yellow-400'
    },
    {
      id: 'effects',
      label: t('previewArea.effects'),
      icon: SparklesIcon,
      hasContent: Array.isArray(selectedVideoClip.effectItems) && selectedVideoClip.effectItems.length > 0,
      color: 'text-cyan-400'
    },
    {
      id: 'video',
      label: t('previewArea.video'),
      icon: VideoCameraIcon,
      hasContent: !!selectedVideoClip.videoUrl,
      color: 'text-red-400'
    },
    {
      id: 'canvas',
      label: '画布',
      icon: RectangleGroupIcon,
      hasContent: true,
      color: 'text-purple-400'
    },
  ] : [];

  const closePopupTab = () => actions.setActiveTab('preview');

  return (
    <div className="flex-1 flex flex-row min-w-0 relative">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6 pb-0 overflow-hidden">
          {!selectedVideoClip ? (
            <div className="relative w-full max-w-4xl aspect-video bg-bg-surface border border-border-subtle rounded-lg overflow-hidden">
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <PhotoIcon className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">{t('previewArea.selectClipToPreview')}</p>
                </div>
              </div>
            </div>
          ) : (
            <PreviewTab />
          )}
        </div>

        {/* Preview Controls */}
        {selectedVideoClip?.videoUrl && state.activeTab === "preview" && (
          <div className="p-4 pt-0">
            <VideoClipEditor />
          </div>
        )}

        {/* Image Duration Controls - 没有视频时显示，空预览状态也允许调整片段时长 */}
        {selectedVideoClip && !selectedVideoClip.videoUrl && state.activeTab === "preview" && (
          <div className="p-4 pt-0">
            <ImageDurationEditor />
          </div>
        )}
      </div>

      {/* Right Side Tabs */}
      {selectedVideoClip && (
        <div className="w-24 flex-shrink-0 flex flex-col items-center justify-start pt-8 py-4 pr-6 gap-4 z-10">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === 'canvas' ? canvasOpen : state.activeTab === tab.id;
            const hasContent = tab.hasContent;

            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'canvas') {
                    setCanvasOpen(true);
                    return;
                  }
                  actions.setActiveTab(tab.id);
                }}
                title={tab.label}
                className={`flex flex-col items-center justify-center w-16 h-16 rounded-lg transition-all duration-200 border gap-1 ${isActive
                  ? 'bg-bg-elevated border-border-default shadow-lg shadow-black/10 scale-105'
                  : 'bg-bg-surface border-border-subtle hover:bg-bg-elevated hover:border-border-default'
                  } ${hasContent ? tab.color : 'text-gray-500'}`}
              >
                <Icon className="w-6 h-6" />
                <span className={`text-xs font-medium ${!hasContent ? 'text-gray-500' : 'text-gray-300'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {selectedVideoClip && (
        <ClipInfiniteCanvasModal
          isOpen={canvasOpen}
          onClose={() => setCanvasOpen(false)}
          videoClip={selectedVideoClip}
          resources={actions.getClipResources(selectedVideoClip.id)}
          episode={episode || state.episode}
          refreshVideoClip={refreshVideoClip}
          onTriggerResourcePoll={actions.triggerResourcePoll}
          editConfig={state.editConfig}
          onEditConfigChange={actions.updateEditConfig}
        />
      )}

      <Transition appear show={state.activeTab === 'images' && !!selectedVideoClip} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePopupTab}>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle shadow-2xl">
                <ImagesTab
                  videoClip={selectedVideoClip!}
                  refreshVideoClip={refreshVideoClip}
                  activeFrameType={modalImageFrameType}
                  onFrameTypeChange={setModalImageFrameType}
                  onRequestClose={closePopupTab}
                />
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={state.activeTab === 'audio' && !!selectedVideoClip} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePopupTab}>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle shadow-2xl">
                <AudioTab
                  videoClip={selectedVideoClip!}
                  refreshVideoClip={refreshVideoClip}
                  onRequestClose={closePopupTab}
                />
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={state.activeTab === 'video' && !!selectedVideoClip} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePopupTab}>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle shadow-2xl">
                <VideoTab
                  videoClip={selectedVideoClip!}
                  refreshVideoClip={refreshVideoClip}
                  editConfig={state.editConfig}
                  onEditConfigChange={actions.updateEditConfig}
                  onRequestClose={closePopupTab}
                />
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={state.activeTab === 'effects' && !!selectedVideoClip} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePopupTab}>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle shadow-2xl">
                <EffectsTab
                  videoClip={selectedVideoClip!}
                  refreshVideoClip={refreshVideoClip}
                  onRequestClose={closePopupTab}
                />
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
